<template lang="pug">
.v-container.suus__event__hero.pa-4.mb-4.mb-sm-8(fluid, v-if="hasErrorInEventOrAttendance")
  v-row.my-6(justify="center", no-gutters)
    v-col(cols="12", sm="8")
      ErrorHero(
        :clickHandler="onBtnClick",
        :titleText="titleText",
        :line1="line1",
        :line2="line2",
      :btnLabel="btnLabel"
      )
.v-container.suus__event__hero.pa-4.mb-8(fluid, v-else)
  div.d-flex.justify-end.py-4
    v-btn(
      text,
      @click="publish",
      color="textColor",
      v-if="!isPublic && eventData?.links?.publish_url",
      :style="btnStyles"
    )
      v-icon(color="textColor") {{ addIcon }}
      span.pl-1 Semesterprogamm
    v-btn(
      text,
      @click="unpublish",
      :disabled="!eventData?.links?.unpublish_url",
      color="primary",
      v-else-if="isPublic",
      :style="btnStyles"
    )
      v-icon(color="primary") {{ doneIcon }}
      span.pl-1 im Semesterprogramm

    v-btn(
      text,
      color="textColor",
      @click="downloadIcs"
      :style="btnStyles",
      v-tooltip="'Als ics-Datei exportieren'"
    )
      v-icon(color="textColor") {{ downloadIcon }}
    EventSheetButton(:isEdit="true" :scrollTo="sheetScrollTo" ref="eventSheetButton")
      template(v-slot:activator="{openSheet}")
        div(:style="{ width: '100%' }")
          v-btn(
            text,
            @click="handleEditEvent(openSheet)",
            color="textColor",
            :style="btnStyles",
            v-tooltip="'Veranstaltung bearbeiten'"
            v-if="AllowedToEdit"
          )
            v-icon(color="textColor") {{ editIcon }}
  div.d-flex
    FlagTag(v-if="levelLabel", :text="levelLabel")
    v-tooltip(
      bottom,
      v-if="eventData && eventData.mailed_by",
      attach=".suus__mailed__tag"
    )
      template(v-slot:activator="{ on, attrs }")
        div.suus__mailed__tag(v-bind="attrs", v-on="on")
          Tag(v-if="eventData && eventData.mailed_at", :text="mailedTag")
            template(v-slot:icon)
              MailedIcon(:size="16", color="textColor")
      <span>{{ mailedDetails }}</span>
  Header(
    :text="title",
    :level="1",
    customClass="mt-1",
    customSkeletonClass="mt-3 mb-6",
    :is-loading="loading"
  )
  v-row
    v-col.mb-10(cols="12", sm="7", xl="6")
      EventCard
    v-col.d-flex.align-center.mb-14(cols="12", sm="5", xl="6")
      EventDetails.px-2.px-sm-0
  v-row
    EventExpansionPanel(@openEditAnimation="openSheetWithAnimation('suus__event-form__attachments')" :viewContext="viewContext")

  v-row.suus__posts-list__header.py-4.pr-8(justify="center")
    Header.suus__posts-list__title(text="Diskussion", :level="5", customClass="mb-0")
  EventPostList(
    v-if="renderPosts"
    :eventId="eventId",
    :total-posts-count="postsCount",
    :can-create-posts="canCreatePosts",
    :can-edit-highlights="!!AllowedToEdit",
    :view-context="viewContext"
    :post-form-warning="postFormWarning"
  )
</template>

<script lang="ts">
/**
 *    CONTEXT ARCHITECTURE
 *    --------------------
 *
 *    X ---------> Y == Y depends on X
 *
 *    -------------------------
 *   |    Event Vuex Store    |
 *   -------------------------
 *              |              \
 *              |               \
 *              |                \
 *              |                 \
 *              |                  v
 *              |            -----------------------------
 *              |           |  OwnUserAttendance-Concern |
 *              |           -----------------------------
 *              |            /
 *              |          /
 *              V        V
 *          -----------------
 *         |  RSVP-Concern  |
 *         ------------------
 *
 */

import { defineComponent, getCurrentInstance, inject, ref, watch } from "vue"
import moment from "moment"
import { createNamespacedHelpers } from "vuex"
const {
  mapState: mapEventState,
  mapActions: mapEventActions,
  mapGetters: mapEventGetters,
  mapMutations: mapEventMutations,
} = createNamespacedHelpers("event")
const { mapGetters: mapMemberGetters, mapActions: mapMemberActions } =
  createNamespacedHelpers("member")
import EventCard from "./EventCard.vue"
import EventDetails from "./EventDetails.vue"
import EventExpansionPanel from "./EventExpansionPanel.vue"
import Header from "../UI/Header.vue"
import ErrorHero from "../UI/ErrorHero.vue"
import FlagTag from "../UI/FlagTag.vue"
import Tag from "../UI/Tag.vue"
import MailedIcon from "../icons/MailedIcon.vue"
import Sheet from "../UI/Sheet.vue"
import EventForm from "../EventForm/EventForm.vue"
import { mdiDownload, mdiPencilOutline } from "@mdi/js"
import { mdiPlus } from "@mdi/js"
import { mdiCheck } from "@mdi/js"
import { ClientAbility, DetailedEvent, LevelAbility } from "suus-api"
import { MediaUploaderService } from "features/media/media-uploader.service"
import { eventsApi } from "store/api"
import EventSheetButton, {
  EventSheetButtonType,
} from "components/Event/EventSheetButton.vue"
import EventPostList from "features/events/posts/EventPostList.vue"
import { ViewContext } from "concerns/useContextName"
import { GalleryService } from "features/gallery/service/gallery.service"
import {
  addGalleyMediumToHighlights,
  removeHighlight,
  reorderHighlights,
} from "features/events/highlights/highlights.functions"
import { EventLightboxService } from "features/events/highlights/lightbox/event-lightbox.service"
import { LightboxService } from "features/gallery/lightbox/lightbox.service"
import { EVENT_STATUS } from "constants/constants"
import { download } from "app/util/download.helper"
import { provideRSVPContext } from "app/modules/events/rsvp-context/use-rsvp-context.concern"
import store from "store"
import { provideOwnUserAttendanceContext } from "app/modules/events/use-own-user-attendance-context.concern"
import useCurrentMember from "concerns/useCurrentMember"
import { usePermissionCheck } from "features/common/permissions/usePermissionCheck"
import { useCurrentContext } from "app/modules/core/current-context/useCurrentContext"
import { GroupAccess } from "components/TopicForm/general-fields/group-access"

export default defineComponent({
  name: "EventPage",
  components: {
    EventPostList,
    EventSheetButton,
    EventCard,
    Header,
    EventDetails,
    EventExpansionPanel,
    ErrorHero,
    FlagTag,
    Tag,
    Sheet,
    EventForm,
    MailedIcon,
  },
  provide(): {
    mediaService: MediaUploaderService
    GalleryService: GalleryService
    EventLightboxService: EventLightboxService
  } {
    const canEdit = ref(!!this.AllowedToEdit)
    watch(
      () => this.AllowedToEdit,
      (val) => {
        canEdit.value = !!val
      }
    )

    const highlightsCount = ref(this.eventData?.highlights_count)
    watch(
      () => this.eventData,
      (val) => {
        highlightsCount.value = val.highlights_count
      }
    )

    const initialShowUploader = ref(
      this.attendStatus === EVENT_STATUS.EVENT_PASSED
    )
    const galleryService = new GalleryService(
      canEdit,
      (galleryId) => addGalleyMediumToHighlights(this.eventId, galleryId),
      (gallery) => removeHighlight(gallery),
      reorderHighlights,
      highlightsCount,
      initialShowUploader
    )

    watch(
      () => this.attendStatus,
      (val) => {
        initialShowUploader.value = val === EVENT_STATUS.EVENT_PASSED
      },
      { immediate: true }
    )

    if (!this.lightboxService) {
      throw new Error("LightboxService not provided")
    }
    const eventLightboxService = new EventLightboxService(
      this.lightboxService,
      galleryService,
      this.eventId,
      this.viewContext
    )

    return {
      mediaService: this.attachmentUploaderService,
      GalleryService: galleryService,
      EventLightboxService: eventLightboxService,
    }
  },
  data() {
    return {
      editIcon: mdiPencilOutline,
      downloadIcon: mdiDownload,
      addIcon: mdiPlus,
      doneIcon: mdiCheck,
      showSheet: false,
      sheetScrollTo: "",
    }
  },
  computed: {
    postFormWarning(): string {
      const hasAdminPermissions = this.hasLevelPermissionOrClientFallback(
        this.eventData?.level_id,
        LevelAbility.LevelAdmin,
        ClientAbility.DomainAdmin
      )
      const isEventInternal =
        this.eventData?.nonmember_access === GroupAccess.INTERNAL
      if (
        hasAdminPermissions.value &&
        isEventInternal &&
        !this.isCurrentMemberInGroup &&
        this.canCreatePosts
      ) {
        return "Die Veranstaltung ist intern und du bist kein Mitglied des Teilnehmerkreises. Du kannst trotzdem Beiträge schreiben, da du ein Admin bist."
      }
      return null
    },
    titleText(): string {
      return this.$i18n.t("components.event.error.titleText").toString()
    },
    line1(): string {
      return this.$i18n.t("components.event.error.line1").toString()
    },
    line2(): string {
      return this.$i18n.t("components.event.error.line2").toString()
    },
    btnLabel(): string {
      return this.$i18n.t("components.event.error.btnLabel").toString()
    },
    mailedTag(): string {
      return this.$i18n.t("components.event.urgent").toString()
    },
    mailedDetails(): string {
      let mailed_at = moment(this.eventData.mailed_at).format(
        "dd. DD.MM.YYYY, HH:mm:ss"
      )
      let context_name = this.getContextName(
        this.eventData?.level_id || undefined,
        this.eventData.mailed_by?.umbrella_name_long,
        this.eventData.mailed_by?.internal_name_long
      )
      const details = `Dringende E-Mail versandt ${
        context_name && `durch ${context_name}`
      } am ${mailed_at} Uhr`
      return details
    },
    title(): string {
      return this.eventData && this.eventData.title
    },
    levelLabel(): string | null | undefined {
      return this.eventData &&
        this.eventData.organization &&
        this.eventData.organization.type === "Level"
        ? this.eventData.organization.name
        : null
    },
    isPublic(): boolean {
      return !!(this.eventData && this.eventData.is_public)
    },
    btnStyles(): object {
      return {
        fontFamily: "system-ui",
        fontWeight: 700,
        letterSpacing: "0.05em",
      }
    },
    AllowedToEdit(): string | null | undefined {
      return (
        this.eventData &&
        this.eventData.links &&
        this.eventData.links.update_url
      )
    },
    loading(): boolean {
      return !!this.eventLoading
    },
    renderPosts(): boolean {
      return !!this.eventData
    },
    canCreatePosts(): boolean {
      return !!this.eventData?.links.posts?.create_url
    },
    postsCount(): number {
      return this.eventData?.posts_count || 0
    },
    //gets event id from url
    eventId(): number {
      return parseInt(this.$route.params.event_id)
    },
    viewContext(): ViewContext {
      return {
        group: this.eventData?.group,
        client_id: this.eventData?.group?.client_id,
        level_id: this.eventData?.group?.level_id,
      }
    },
    ...mapEventState({
      eventData: (state: any): DetailedEvent => state.currentEvent.data,
      eventLoading: (state: any): boolean => state.currentEvent.loading,
    }),
    ...mapEventGetters(["hasError"]),
    ...mapMemberGetters(["canPublish"]),
    hasErrorInEventOrAttendance(): boolean {
      return this.hasError || !!this.ownUserAttendanceError || !!this.error
    },
  },
  methods: {
    getContextName(
      level_id?: number,
      umbrella_name?: string,
      client_name?: string
    ): string | undefined {
      return level_id ? umbrella_name : client_name
    },
    onBtnClick(): void {
      window.location.href = "/"
    },
    openSheetWithAnimation(selector: string): void {
      ;(this.$refs.eventSheetButton as EventSheetButtonType).openSheet()
      this.showSheet = true
      this.sheetScrollTo = selector
      this.setupForm()
    },
    publish(): void {
      this.addPublic(this.eventData.id)
    },
    unpublish(): void {
      this.deletePublic(this.eventData.id)
    },
    async handleEditEvent(openSheet: () => void): Promise<void> {
      await this.setupForm()
      openSheet()
    },
    async setupForm(): Promise<void> {
      this.attachmentUploaderService.reset()
      try {
        const attachments = await eventsApi.getEventAttachments(
          this.eventData.id.toString()
        )
        this.UPDATE_EVENT_ATTRIBUTE({
          key: "attachments",
          val: attachments?.data,
        })
      } catch (e) {
        console.error("Error while fetching attachments: ", e)
      }
      this.setFormData()
    },
    ...mapEventMutations(["UPDATE_EVENT_ATTRIBUTE"]),
    ...mapEventActions([
      "getEvent",
      "setFormData",
      "addPublic",
      "deletePublic",
    ]),
    ...mapMemberActions(["getCurrentMember"]),
  },
  setup() {
    const attachmentUploaderService = new MediaUploaderService()
    const lightboxService: LightboxService = inject("lightboxService")
    const downloadIcs = () => {
      download(window.location.origin + window.location.pathname + ".ics")
    }

    const currentInstance = getCurrentInstance()
    const eventId = currentInstance.proxy?.$route.params.event_id

    const event$ = store.dispatch("event/getEvent", eventId)

    const ownUserAttendanceContext = provideOwnUserAttendanceContext(eventId)
    const { preloadRSVPs, error } = provideRSVPContext(eventId, async () => {
      const event = await eventsApi.getEvent(eventId).then((res) => res.data)
      return {
        attendsCount: event.attends_count ?? 0,
        guestsCount: event.guests_count ?? 0,
        declinesCount: event.declines_count ?? 0,
        pendingsCount: event.pendings_count ?? 0,
      }
    })

    const { hasLevelPermissionOrClientFallback } = usePermissionCheck()
    const { isCurrentMemberInGroup: checkIsCurrentMemberInGroup } =
      useCurrentContext()
    const isCurrentMemberInGroup = ref(false)

    event$.then((_event: DetailedEvent) => {
      preloadRSVPs()

      const { currentMemberPromise } = useCurrentMember()
      currentMemberPromise.then((currentMember) => {
        ownUserAttendanceContext.initializeOwnUserStatus(
          currentMember.value.personal_record.id.toString()
        )

        const isInGroupComputed = checkIsCurrentMemberInGroup(_event.group_id)
        watch(
          isInGroupComputed,
          (val) => {
            isCurrentMemberInGroup.value = val
          },
          { immediate: true }
        )
      })
    })

    return {
      attachmentUploaderService,
      lightboxService,
      downloadIcs,
      attendStatus: ownUserAttendanceContext.eventAttendStatus,
      ownUserAttendanceError: ownUserAttendanceContext.ownUserStatusError,
      ownUserStatusLoading: ownUserAttendanceContext.ownUserStatusLoading,
      error: error,
      hasLevelPermissionOrClientFallback,
      isCurrentMemberInGroup,
    }
  },
})
</script>

<style lang="sass">
.suus__event__hero
  max-width: 1280px
  margin: auto

.suus__event__hero
  width: 100% !important

.suus__posts-list__header
  border-top: 1px solid #C4C4C4
  border-bottom: none
</style>
